export const common = {
  loadingProject: "Loading project...",
  loadingChapter: "Loading...",
  loadingMessages: "Loading Messages...",
  export: "Export",
  exportAs: "Export as",
  exporting: "Exporting...",
  fileName: "File Name",
  cancel: "Cancel",
  submit: "Submit",
  addFiles: "Add files",
  clearChat: "Clear chat",
  clear: "Clear",
  close: "Close",
  confirm: "Confirm",
  loading: "Loading",
  optional: "Optional",
  notifications: {
    title: "Notifications",
    markAllAsRead: "Mark all as read",
    loadFailed: "Couldn't load notifications",
    chatResponded: "Leo replied: {{project}}",
    chatRespondedGeneric: "Leo replied",
    analysisReady: "Analysis ready: {{project}}",
    analysisReadyGeneric: "Analysis ready",
    analysisReadySubtitle: "Your project analysis is ready to view.",
    triggerUnread: "Notifications, {{count}} unread",
    markRead: "Mark as read",
    markGroupRead: "Mark {{name}} as read",
    groupCount_one: "{{count}} notification",
    groupCount_other: "{{count}} notifications",
    groupUnread: "{{count}} unread",
    unread: "Unread",
    loadingList: "Loading notifications",
    loadFailedHint: "Check your connection and try again.",
    retry: "Try again",
    caughtUpTitle: "You're all caught up",
    caughtUpDescription: "Finished analyses and Leo's replies show up here."
  },
  timeAgo: {
    justNow: "Just now",
    minutes: "{{count}}m ago",
    hours: "{{count}}h ago",
    days: "{{count}}d ago",
    weeks: "{{count}}w ago",
    months: "{{count}}mo ago"
  },
  status: {
    pass: "Pass",
    conditional: "Conditional",
    fail: "Fail",
    unvalidated: "Unvalidated",
    not_defined: "Undefined"
  },
  versions: {
    newTitle: "New Version",
    renameTitle: "Rename Version",
    deleteTitle: "Delete Version",
    nameLabel: "Version Name",
    namePlaceholder: "New version...",
    baseOn: "Base On",
    scope: "Scope",
    frameOnly: "Frame Only",
    frameOnlyDescription: "Just Project Setup and Case Framing, no analysis.",
    fullVersion: "Full Version",
    fullVersionDescription: "Everything, including analysis and results.",
    create: "Create Version",
    save: "Save",
    confirm: "Confirm",
    close: "Close",
    untitledProject: "Project",
    actions: "Version actions",
    rename: "Rename",
    delete: "Delete",
    deleteQuestion: "Delete {{version}} from {{project}}?",
    deleteQuestionNoProject: "Delete {{version}}?",
    deleteBody: "All version data and settings will be permanently deleted.",
    deletedToast: "Version {{name}} deleted.",
    createdToast: "Version {{name}} created.",
    renamedToast: "Version renamed to {{name}}.",
    removedToast: "Removed {{name}}.",
    restoredToast: "Restored {{name}}.",
    undo: "Undo",
    view: "View",
    errors: {
      noProject: "Could not determine project to duplicate",
      nameRequired: "Name required",
      renameFailed: "Failed to rename",
      restoreFailed: "Failed to restore version",
      deleteFailed: "Failed to delete version",
      createFailed: "Couldn't create the version",
      duplicateFailed: "Failed to duplicate"
    }
  },
  versionMenu: {
    versions: "Versions",
    latest: "Latest",
    draft: "Draft",
    trigger: "Version: {{name}}. Change version",
    rowActions: "{{name}} actions",
    rename: "Rename",
    remove: "Remove",
    removing: "Removing {{name}}",
    keepOneVersion: "A project keeps at least one version.",
    newVersion: "New version",
    createVersion: "Create version",
    creating: "Creating…",
    firstVersionDescription:
      "Keep the project as it is and work on a copy. Both stay in the project, and you can switch between them.",
    newVersionDescription: "A copy of a version that you can change without touching the original.",
    name: "Name",
    nameTaken: "There’s already a version with that name.",
    basedOn: "Based on",
    draftNoAnalysis: "Draft, no analysis yet",
    copy: "Copy",
    frameOnly: "Frame only",
    frameOnlyDescription: "Copies the project setup and case frame. Review them, then launch the analysis again.",
    fullAnalysis: "Full analysis",
    fullAnalysisDescription: "Copies everything, including the analysis and its results.",
    noAnalysisYet: "No version has an analysis yet.",
    renameTitle: "Rename version",
    renameDescription: "Only the name changes. The version’s content stays as it is.",
    save: "Save",
    saving: "Saving…"
  },
  labsMode: {
    title: "Labs Mode",
    description: "Enable experimental features and early access functionality",
    menuDescription: "Labs Mode gives you access to experimental features that are currently being tested",
    enableFailed: "Failed to enable labs mode",
    disableFailed: "Failed to disable labs mode"
  },
  userMenu: {
    profile: "Profile",
    organization: "Organization",
    privacyNotice: "Privacy notice",
    termsOfUse: "Terms of Use",
    cookieNotice: "Cookie Notice",
    signOut: "Sign out",
    signOutError: "Failed to sign out. Please try again.",
    menuLabel: "{{name}} menu",
    organizationSettings: "Organization settings",
    organizationSettingsDescription: "Name, logo, AI model and instructions",
    teamMembers: "Team members",
    teamMembersDescription: "Invite people and manage roles",
    account: "Account",
    yourProfile: "Your profile",
    legal: "Legal"
  }
} as const;

export const projectHome = {
  sidebar: {
    home: "Home",
    projectHome: "Project home",
    foundation: "Foundation",
    executiveSummary: "Executive Summary",
    innoveraMemo: "Innovera Memo",
    deepDives: "Deep Dives",
    marketAnalysis: "Market Analysis",
    strategyAnalysis: "Strategy Analysis",
    financialAnalysis: "Financial Analysis",
    appendices: "Appendices",
    hiddenChapters: "Hidden Pages",
    context: "Context",
    frame: "Frame",
    projectSetup: "Project Setup",
    caseFrame: "Case Frame",
    valueChain: "Value Chain",
    vault: "Vault",
    artifacts: "Artifacts",
    presentations: "Presentations",
    exports: "Exports",
    modelLedger: "Model Ledger",
    rqaSim: "Innovera RQASim",
    rqa: "Innovera RQA",
    collapse: "Collapse",
    expand: "Expand",
    collapseSidebar: "Collapse sidebar",
    expandSidebar: "Expand sidebar",
    locked: "Locked for now",
    lockedIcon: "Locked",
    navLabel: "Project"
  },
  rqaSim: {
    empty: "This run produced no working model the page could stand behind. Relaunch the analysis to build one.",
    emptyNoLedger: "This project has no model ledger yet, so there is nothing to move.",
    ownSettings: "Your own settings. These are not the numbers the analysis published — reset to return to them.",
    showing: "What the page is showing",
    quickSets: "Quick sets",
    inView: "{{shown}} of {{total}} in view.",
    whereItLands: "Where it lands",
    reset: "Reset to the analysis",
    otherInputs: "Other inputs",
    published: "The analysis published {{value}}",
    held: "Held at the published value: its formula does not resolve at these settings.",
    noReturn: "No rate at these settings: the case never comes back to zero.",
    notInView: "Nothing this input prices is in view.",
    outsideBand: "Outside the band the analysis supports ({{low}} to {{high}}).",
    unresolvedOne:
      "{{count}} figure could not be recomputed at these settings and still shows the analysis's value: {{keys}}",
    unresolvedMany:
      "{{count}} figures could not be recomputed at these settings and still show the analysis's value: {{keys}}",
    custom: "Custom",
    customNote: "No scenario — these are your own settings.",
    noReturnOne:
      "{{count}} figure has no rate at these settings, because the case never comes back to zero, and is shown as a dash: {{keys}}",
    noReturnMany:
      "{{count}} figures have no rate at these settings, because the case never comes back to zero, and are shown as a dash: {{keys}}",
    pinnedBy: "Pinned by {{name}}",
    pinFull: "A project pins at most {{count}} figures.",
    configure: "Configure",
    configureClose: "Close the settings",
    open: "Open Innovera RQASim",
    exportReport: "Export Report as PDF",
    advanced: "Advanced",
    variableCount: "{{count}} variables",
    searchVariables: "Search variables",
    wasValue: "was {{value}}",
    done: "Done",
    addWidget: "Add a widget",
    describeWidget: "Describe the widget",
    describeHint: "Say what you want to see. It is built from the variables in the current configuration.",
    describePlaceholder: "e.g. Capital per MW for every option, as bars",
    describing: "Building the widget…",
    pinInstead: "Or pin a figure",
    filters: "Filters",
    filterGroups: "Groups",
    changedOnly: "Changed only",
    resetShort: "Reset",
    noVariables: "No variable matches.",
    unpin: "Remove this widget",
    removedTile: "Removed {{name}}",
    undo: "Undo",
    body: "What follows from it"
  },
  risks: {
    title: "Innovera RQA",
    tabOverview: "Overview",
    tabTable: "Risks",
    columnRisk: "Risk",
    columnImpact: "Impact",
    columnUncertainty: "Uncertainty",
    columnStatus: "Status",
    empty: "This project has no risks yet. Add one, or they are generated together with the analysis.",
    addTitle: "Add Risk",
    editTitle: "Edit Risk",
    addressTitle: "Address Risk",
    addressRisk: "Address Risk",
    addressed: "Addressed",
    fieldTitle: "Title",
    fieldTitlePlaceholder: "Name the risk",
    fieldWhatMustHoldTrue: "What must hold true",
    fieldWhyItMatters: "Why it matters",
    fieldImpact: "Impact",
    fieldUncertainty: "Uncertainty",
    summaryTitle: "Where the risks stand",
    summaryTotal: "Risks",
    summaryOpen: "Open",
    summaryCruxOpen: "Crux, open",
    summaryAddressed: "Addressed",
    graphTitle: "Impact and uncertainty",
    nextTitle: "To address next",
    allAddressed: "Every risk is addressed.",
    axisImpact: "Impact of the verdict",
    axisUncertainty: "Uncertainty: how far from settled",
    quadrant: {
      pinDown: "Pin down",
      crux: "Crux",
      noted: "Noted",
      monitor: "Monitor"
    },
    level: {
      low: "Low",
      medium: "Medium",
      high: "High"
    },
    plan: "Plan",
    planPlaceholder: "What are you going to do about this risk?",
    results: "Results",
    resultsPlaceholder: "What happened once you did",
    attachFile: "Attach File",
    submit: "Submit",
    save: "Save",
    edit: "Edit",
    cancel: "Cancel",
    created: "Risk added",
    updated: "Risk updated",
    saveFailed: "Something went wrong. Please try again.",
    fileOpenFailed: "Could not open the file. Please try again."
  },
  leo: {
    title: "Leo",
    placeholderGenerating: "Chat opens once the analysis is ready…",
    updatedChapters: "Updated pages: {{chapters}}",
    updatedSlides_one: "✓ Updated slide {{slides}}",
    updatedSlides_other: "✓ Updated slides {{slides}}",
    slides: "the slides",
    stillRunning:
      "Leo is still finishing the previous request, so your message was not sent. It is back in the message box; send it once Leo is done.",
    send: "Send",
    clear: "Clear",
    clearTitle: "Clear this chat?",
    cancel: "Cancel",
    chatCleared: "Chat cleared",
    placeholderAnything: "Ask Leo anything",
    welcomeTitle: "How can I help today?",
    welcomeSubtitle: "Ask about this project, its sources and reports.",
    frameWelcomeSubtitle: "Tell Leo what to change in the case frame.",
    suggestionStands: "Summarize where this project stands",
    suggestionUnproven: "What is still unproven?",
    suggestionNext: "What should we do next?",
    welcomeSubtitleProjects: "Ask about your projects, sources and reports.",
    suggestionActive: "Summarize my active projects",
    suggestionAttention: "Which projects need my attention this week?",
    disclaimer: "Leo can make mistakes. Check important findings.",
    busy: "Leo is working, you can send when it's done.",
    hide: "Hide Leo",
    ask: "Ask Leo",
    waiting: {
      title: "How can I help while you wait?",
      subtitle: "Let me know if you'd like to add anything or learn how the analysis works.",
      ask: {
        ready: "When will it be ready?",
        deliverables: "What will I get?",
        change: "Can I still change things?"
      },
      answer: {
        ready: "In {{eta}}. Every page is ready at the same time, and I'll notify you when it is.",
        readySoon: "Any minute now. Every page is ready at the same time, and I'll notify you when it is.",
        deliverables:
          "Every page arrives at once: Project Home with the verdict, the Innovera Memo and the analysis pages behind it, and, where the analysis builds them, the risk simulation and the Model Ledger.",
        change:
          "Not while it runs: the Case Frame and the Vault are locked. Cancel the analysis to go back and change them, then launch again.",
        locked:
          "The analysis is running on the case frame as it was launched, so it's locked for now. To change it, cancel the analysis."
      }
    },
    updatingAnalysis: "Updating the analysis. The current content stays on screen until the update is ready.",
    images: {
      attach: "Attach images",
      remove: "Remove {{name}}",
      unsupported: "Only PNG, JPEG, WebP and GIF images can be attached.",
      tooLarge: "Each image must be 5 MB or smaller.",
      tooMany: "You can attach up to {{count}} images to a message.",
      unavailable: "This image can't be shown right now."
    },
    status: {
      starting: "Leo is getting started…",
      reading: "Reading {{chapter}}…",
      editing: "Editing {{chapter}}…",
      readingSlide: "Reading slide {{slide}}…",
      editingSlide: "Updating slide {{slide}}…",
      designing: "Working on the slide design…",
      searching: "Searching the memo for “{{query}}”…",
      thinking: "Thinking…",
      replying: "Writing the reply…"
    }
  },
  modelLedger: {
    title: "Model Ledger",
    searchPlaceholder: "Search variables",
    clearSearch: "Clear search",
    filter: "Filter",
    reset: "Reset",
    filterRole: "Role",
    filterSource: "Source",
    more: "More actions",
    downloadModel: "Download model.py",
    figureCardDependsOn: "Depends on",
    columnVariable: "Variable",
    columnValue: "Value",
    columnSource: "Source",
    columnRole: "Role",
    columnWhatThisIs: "What this is",
    sourceCalculated: "Calculated",
    sourceBrief: "Case brief",
    sourceDocument: "Vault",
    originDocument: "Vault document",
    downloading: "Downloading…",
    sourceWeb: "Web",
    sourced: "Sourced",
    sourceAssumption: "Assumption",
    sourceOther: "Stated",
    roleObjective: "Objective",
    roleDecision: "Decision",
    roleConstraint: "Constraint",
    roleAssumption: "Assumption",
    roleParameter: "Parameter",
    roleOutput: "Output",
    roleIntermediate: "Intermediate",
    whatThisIs: "What this is",
    factBinding: "Binding",
    factProvenance: "Provenance",
    factConfidence: "Confidence",
    factTreatment: "Treatment",
    factAsOf: "As of",
    factAssumedFrom: "Assumed from",
    factRelatedTo: "Moves with",
    provenanceFrame: "set in the frame",
    provenanceClient: "from the client",
    provenanceResearch: "researched",
    provenanceBenchmark: "from the cohort",
    provenanceExpert: "from an expert",
    provenanceFeed: "from a live feed",
    provenanceComputed: "computed here",
    provenanceEngine: "assumed by the engine",
    confidenceValidated: "validated",
    confidenceEstimated: "estimated",
    bindingHard: "must hold",
    bindingSoft: "target",
    treatmentHeld: "held",
    treatmentSwept: "swept",
    treatmentSolved: "solved for",
    treatmentTracked: "tracked",
    unverified: "Unverified",
    unverifiedWhy:
      "The run's model.py printed {{printed}}; the platform's evaluator gives {{computed}}, which the page shows. The two read this formula differently, and engineering has been alerted.",
    movedBy: "Changed in chat by {{by}}, {{date}}: {{from}} → {{to}}",
    movedEarlier: "Changed in chat earlier: was {{from}}",
    moved: "Changed in chat, {{date}}: {{from}} → {{to}}",
    whereFrom: "Where this comes from",
    drives: "What this drives",
    checksTitle: "Checks the model makes",
    checksSummary: "{{holding}} of {{total}} hold",
    checkHolds: "Holds",
    checkFails: "Does not hold",
    checkNotEvaluated: "Could not be checked",
    valueNotComputed: "Could not be computed",
    whyNotComputed: "Why this could not be computed",
    empty:
      "This project has no model ledger yet. The ledger is produced together with the analysis when the project is generated.",
    noMatches: "No variables match."
  },
  valueChain: {
    readOnly: "Read-only",
    title: "Value chain",
    generate: "Generate",
    regenerate: "Regenerate",
    generating: "Generating...",
    generatedToast: "Value chain generated",
    regeneratedToast: "Value chain regenerated",
    generationFailed: "Generation failed. Please try again.",
    loadErrorTitle: "Couldn't load the value chain",
    loadErrorBody: "Something went wrong while fetching it. Check your connection and try again.",
    retry: "Retry",
    emptyTitle: "No value chain yet",
    emptyBodyEditor: "Generate the value chain from this project's analysis, vault documents and overview.",
    emptyBodyViewer: "A project editor can generate it from this project's analysis, vault documents and overview.",
    footerNote:
      "Generated from the project's analysis and vault; annotations quote the analysis above. Regenerate to reflect the latest content.",
    upstream: "Upstream",
    downstream: "Downstream",
    scrollEarlier: "Scroll to earlier stages",
    scrollLater: "Scroll to later stages",
    aboutPosition: "About {{label}}",
    whoHoldsTitle: "Who holds each stage, and what characterizes it?",
    colStage: "Stage",
    colWhoHolds: "Who holds it today",
    colCharacterizes: "What characterizes it",
    annotation: {
      concentration: "Concentration",
      rivalry: "Rivalry",
      inputDifficulty: "Hard to source",
      valueCapture: "Value capture"
    },
    position: {
      base: "Base Case",
      candidate: "Option",
      recommended: "Recommended",
      rejected: "Rejected"
    },
    owner: {
      ours: "Us, today",
      suppliers: "Our Suppliers",
      customers: "Our Customers",
      others: "Third Parties",
      nobody: "Unclaimed"
    },
    errors: {
      notEnabled: "Value chain is not enabled for this organization",
      inProgress: "Value chain generation is already in progress",
      noContent: "The project has no analysis or case brief yet — generate project content first",
      generationFailed: "Generation failed. Please try again."
    }
  },
  chapterBlocks: {
    theNumbers: "The Numbers",
    rowsNotCharted_one: "{{count}} row is not charted; it is in the table below.",
    rowsNotCharted_other: "{{count}} rows are not charted; they are in the table below.",
    figuresErrorTitle: "Couldn't load this page's figures",
    figuresErrorBody:
      "This page's numbers are written from the Model Ledger, which did not load. Check your connection and try again.",
    empty: "This page has no content yet.",
    retry: "Retry"
  },
  chapterHeader: {
    aq: "AQ",
    aqTitle: "Analysis Questions",
    iq: "IQ",
    iqTitle: "Intelligence Questions",
    claims: "Claims",
    claimsTitle: "Claims Extraction",
    comments: "Comments",
    commentsTitle: "Comments",
    toolWithCount: "{{label}}, {{count}}",
    vault: "Vault",
    vaultTitle: "Document Vault",
    fullWidth: "Full width",
    defaultWidth: "Default width",
    moreOptions: "More options",
    markdown: "Markdown",
    copy: "Copy",
    paste: "Paste",
    noContentToCopy: "No content to copy",
    figuresNotReady: "This page's figures are not loaded yet. Try again in a moment.",
    markdownCopied: "Markdown copied to clipboard",
    copyFailed: "Failed to copy markdown",
    noContentInClipboard: "No content in clipboard",
    editorNotReady: "Editor not ready",
    markdownPasted: "Markdown pasted successfully",
    pasteFailed: "Failed to paste markdown"
  },
  comments: {
    title: "Comments",
    close: "Close comments",
    loading: "Loading comments…",
    emptyTitle: "No comments yet",
    emptyDescription: "Select text on the page to comment on it, or leave a note below.",
    showInChapter: "Show on the page",
    resolve: "Resolve",
    actions: "Comment actions",
    edit: "Edit",
    delete: "Delete",
    cancel: "Cancel",
    addComment: "Add a comment",
    postComment: "Post comment",
    reply: "Reply",
    sendReply: "Send reply",
    editComment: "Edit your comment",
    save: "Save",
    replyCount_one: "{{count}} reply",
    replyCount_other: "{{count}} replies"
  },
  header: {
    document: "Document",
    presentation: "Presentation",
    presentationDisabledTooltip: "Presentation will be available once the Document generation is complete.",
    exportProject: "Export Project",
    exportPresentation: "Export Presentation",
    duplicateProject: "Duplicate Project",
    duplicateConfirm: "Are you sure you want to duplicate this project?",
    duplicateAction: "Duplicate",
    duplicateFailed: "Failed to duplicate project",
    duplicateSuccess: "Project duplicated successfully",
    switchedTo: "Switched to {{name}}.",
    exportDocSuccess: "Document exported successfully",
    exportDocFailed: "Failed to export document",
    exportDownloadFailed: "Failed to download file",
    exportFormatUnavailable: "{{format}} is not available for this presentation",
    brandLabel: "Innovera home",
    breadcrumb: "Breadcrumb"
  },
  overview: {
    opportunityFrame: "Opportunity Frame",
    successCriteria: "Success Criteria",
    keyInsights: "Key Insights",
    nextSteps: "Next Steps",
    target: "Target: {{target}}",
    outOfDate: "Out of date",
    outOfDateTooltip: "Out of date - refresh to update",
    refresh: "Refresh",
    refreshing: "Refreshing…",
    updated: "Updated {{date}}",
    failedToRefresh: "Failed to refresh",
    generating: "Generating overview",
    legendCount: "{{value}} {{status}}",
    whereItStands: "Where it stands",
    question: "Question",
    verdict: "Verdict",
    recommendation: "Recommendation",
    recommendationPending: "The recommendation appears once the analysis has run.",
    criteriaMet: "{{score}} of {{total}} criteria met",
    sourceCount_one: "<count>{{count}}</count> source",
    sourceCount_other: "<count>{{count}}</count> sources",
    chaptersReady: "<ready>{{ready}}</ready> of <total>{{total}}</total> pages ready"
  },
  chat: {
    title: "Chat",
    hide: "Hide chat",
    chatDisabled: "Chat is disabled",
    clearChatConfirm: "Are you sure you want to clear the chat?",
    clearThisChat: "Clear this chat?",
    chatCleared: "Chat cleared"
  },
  caseFrame: {
    relaunch: "Relaunch Analysis",
    relaunching: "Relaunching…",
    relaunchDescription:
      "A new version will be created with your changes applied. You'll be redirected automatically and the analysis will restart. This may take 10–30 minutes.",
    relaunchConfirm: "Create & Relaunch",
    empty: "This project has no case frame yet.",
    relaunchFailed: "Failed to relaunch analysis",
    saveFailed: "Failed to save changes. Please try again.",
    instructionsSaveFailed: "Failed to save instructions. Please try again."
  },
  projectSetup: {
    visibility: "Visibility",
    organization: "Organization",
    private: "Private",
    language: "Language",
    version: "Version",
    versionInitial: "Initial",
    vault: "Vault",
    attachment: "Attachment",
    attachments: "Attachments",
    instructions: "Instructions",
    namePlaceholder: "Project Name",
    descriptionPlaceholder: "What would be a short description of this project?",
    dimensionAdd: "Add",
    dimensionAddValue: "Add value",
    dimensionSave: "Save",
    photoUpload: "Upload project photo",
    photoExpand: "Expand project photo",
    photoInvalidType: "Only JPG, PNG and WebP images are allowed.",
    photoTooLarge: "The image must not exceed 5MB.",
    photoUploadFailed: "Could not upload the photo. Please try again.",
    languageName: {
      english: "English",
      japanese: "Japanese"
    }
  },
  dashboard: {
    title: "Dashboard",
    chapter: "Chapter",
    taskInProgress: "Task In Progress",
    complete: "Complete",
    view: "View",
    documentsBeingAnalyzed: "Your documents are being analyzed",
    analyzeTimeEstimate: "It usually takes 1-3 minutes"
  },
  generation: {
    backToProjectList: "Back to Project List",
    analysisInProgressTitle: "Analysis in Progress",
    analysisInProgressDescription:
      "We're reviewing your documents and building a full analysis. You can safely navigate away. We'll notify you when it's ready.",
    failedTitleRateLimit: "The analysis hit a usage limit",
    failedDescriptionRateLimit:
      "Capacity is limited right now, so the analysis couldn't finish. Retrying continues where it left off — the pages already written are kept.",
    failedTitleTimeout: "The analysis ran out of time",
    failedDescriptionTimeout:
      "This one took longer than the time limit allows. Retrying resumes from the pages that already finished.",
    failedTitleGeneric: "The analysis couldn't be completed",
    failedDescriptionGeneric:
      "Something went wrong while building the analysis and our team has been notified. You can retry, or view whatever finished so far.",
    failedRetry: "Retry analysis",
    readyIn: "Ready in",
    readyInHours: "~{{hours}}h",
    readyInMinutes: "~{{minutes}} min",
    readyInHoursMinutes: "~{{hours}}h {{minutes}} min",
    readySoonLabel: "Ready",
    readySoon: "soon",
    setupStarted:
      "Analysis started. We're reviewing your documents and building a full analysis. You can safely navigate away. We'll notify you when it's ready.",
    workingOnNow: "Working on now:",
    setupPhase: {
      orienting: "Reviewing your documents and brief",
      modeling: "Building the underlying model",
      ledger: "Computing the numbers",
      chapters: "Writing the analysis",
      twin: "Assembling the interactive model",
      storing: "Finalizing the analysis"
    },
    queuedTitle: "{{title}} is Queued",
    queuedFallbackTitle: "This page",
    queuedStartsAfter: "It starts as soon as these pages finish:",
    queuedStartsAfterOne: "It starts as soon as this page finishes:",
    queuedStartsShortly: "It will start shortly.",
    generatingChapter: "Generating page",
    progressStarted:
      "Analysis started and will take 10-30 minutes. You can safely navigate from this page and come back later.",
    progressOrder: "Pages are written in dependency order, so they finish out of sequence.",
    openChapter: "Open {{name}}",
    cancelAnalysis: "Cancel Analysis",
    cancelAnalysisDescription:
      "This will stop the analysis and return your project to the Case Frame stage. Any progress will be lost.",
    keepGenerating: "Keep Generating",
    close: "Close"
  },
  exports: {
    empty: "Nothing exported yet",
    open: "Open",
    download: "Download",
    downloadFailed: "Failed to download file",
    deck: "Deck",
    memo: "Memo",
    emptyDescription: "Export the memo or the deck to share it; every export is kept here.",
    exportedOnBy: "Exported {{date}} by {{author}}",
    exportedOn: "Exported {{date}}",
    exportedBy: "Exported by {{author}}",
    rowActions: "{{name}} actions"
  },
  exportOptions: {
    close: "Close",
    type: "Type",
    fullReport: "Full Report",
    fullReportPages: "Every page, in full.",
    fullReportChapters: "Every chapter, in full.",
    summary: "Summary",
    summaryDescription: "Condensed overview only.",
    include: "Include",
    projectFrame: "Project Frame",
    projectInstructions: "Project Instructions",
    analysisQuestions: "Analysis Questions and Intelligence Questions",
    vaultFilesAndLinks: "Vault Files and Links"
  },
  vaultPage: {
    downloadAll: "Download All",
    downloading: "Downloading…",
    downloadAllFailed: "Failed to download files",
    empty: "No files in the vault yet",
    emptyDescription: "Upload the documents, data and transcripts this project should draw on.",
    uploading: "Uploading: {{files}}",
    uploadingMeta: "Uploading…",
    addedOn: "Added {{date}}",
    addedBy: "by {{name}}",
    rowActions: "{{name}} actions",
    processing: "Processing…",
    processFailed: "Failed to process",
    openLink: "Open link",
    download: "Download",
    downloadFailed: "Failed to download file",
    retryProcessing: "Retry processing",
    delete: "Delete",
    deleteTitle: "Delete Document",
    deleteDescription: "Are you sure you want to delete this document?",
    add: "Add",
    files: "Files",
    waitForUploads: "Wait for current uploads to finish",
    filesLimit: "Up to {{maxFiles}} files, {{maxMb}} MB in total",
    webLinkDescription: "Add a page by its address",
    uploadLimit: "Upload up to {{maxFiles}} files (max {{maxMb}}MB total)",
    webLink: "Web Link",
    addWebLink: "Add Web Link",
    invalidUrl: "Please enter a valid URL",
    duplicateLink: "This web link has already been added",
    webLinkAdded: "Web link added and processing started",
    webLinkFailed: "Failed to add web link"
  },
  recordChip: {
    high: "High",
    medium: "Medium",
    low: "Low",
    notNow: "Not now",
    no: "No",
    yes: "Yes",
    fail: "Fail",
    failed: "Failed",
    pass: "Pass",
    passed: "Passed",
    rejected: "Rejected",
    recommended: "Recommended",
    weak: "Weak",
    weakened: "Weakened",
    strong: "Strong",
    negative: "Negative",
    positive: "Positive",
    critical: "Critical",
    blocked: "Blocked",
    moderate: "Moderate",
    partial: "Partial",
    partially: "Partially",
    conditional: "Conditional",
    watch: "Watch",
    unclear: "Unclear",
    atRisk: "At risk",
    confirmed: "Confirmed",
    onTrack: "On track"
  },
  instructionsDialog: {
    title: "Instructions",
    sections: "Instruction sections",
    general: "General",
    presentation: "Presentation",
    chapters: "Pages",
    hasInstructions: "Has instructions",
    projectTitle: "Project instructions",
    projectDescription: "Guidance Leo follows on every page of this project.",
    projectPlaceholder: "For example: focus on US health systems, cite public sources only.",
    outlineTitle: "Presentation instructions",
    outlineDescription: "How the presentation is drafted. Leave it empty for the default.",
    outlinePlaceholder: "For example: ten slides, lead with the recommendation.",
    chapterTitle: "Page instructions",
    chapterDescription: "Guidance for this page only, on top of the project instructions.",
    chapterPlaceholder: "Write instructions for {{name}}",
    thisChapter: "this page",
    close: "Close",
    cancel: "Cancel",
    save: "Save changes",
    saving: "Saving…"
  },
  instructionsModal: {
    title: "Instructions",
    general: "GENERAL",
    projectInstructions: "Project Instructions",
    presentation: "PRESENTATION",
    outlineInstructions: "Presentation Instructions",
    pages: "PAGES",
    pageInstructions: "Page Instructions",
    projectPlaceholder: "Write Project Instructions",
    outlinePlaceholder: "Customize how presentation slides are generated. Leave empty for default behavior.",
    pagePlaceholder: "Write instructions for {{name}}",
    thisPage: "this page",
    saving: "Saving...",
    saveChanges: "Save Changes",
    saveFailed: "Failed to save instructions",
    saved: "Instructions saved"
  },
  presentation: {
    backToOutline: "Back to Outline",
    openInGamma: "Open in Gamma",
    outlineTitle: "Presentation Outline",
    created: {
      justNow: "Created just now",
      minutes: "Created {{count}} min ago",
      hourOne: "Created {{count}} hour ago",
      hourMany: "Created {{count}} hours ago",
      dayOne: "Created {{count}} day ago",
      dayMany: "Created {{count}} days ago"
    },
    refreshed: {
      justNow: "Refreshed just now",
      minutes: "Refreshed {{count}} min ago",
      hourOne: "Refreshed {{count}} hour ago",
      hourMany: "Refreshed {{count}} hours ago",
      dayOne: "Refreshed {{count}} day ago",
      dayMany: "Refreshed {{count}} days ago"
    },
    updatingSlides: "Updating slides...",
    previewSlides: "Preview Slides",
    creating: "Creating...",
    refreshing: "Refreshing...",
    createSlides: "Create Slides",
    refreshSlides: "Refresh Slides",
    loadingOutline: "Loading presentation outline...",
    refreshingOutline: "Refreshing Outline",
    creatingOutline: "Creating Outline",
    analyzingLine1: "Analyzing your document and",
    analyzingLine2: "structuring the outline",
    startTitle: "Start Your Presentation",
    startLine1: "Create a slide outline from your document, then",
    startLine2: "convert it into a fully designed presentation",
    createOutline: "Create Outline",
    refreshOutline: "Refresh Outline",
    slides: "Slides",
    addSlide: "Add slide",
    newSlide: "New Slide",
    deleteSlide: "Delete slide",
    untitledSlide: "Untitled slide",
    noSlides: "No slides yet. Create an outline to get started",
    slideNumber: "Slide {{number}}",
    slideTitlePlaceholder: "Slide Title",
    keyPointsPlaceholder: "• Key point 1\n• Key point 2\n• Key point 3",
    bold: "Bold (Ctrl+B)",
    italic: "Italic (Ctrl+I)",
    bulletList: "Bullet List",
    numberedList: "Numbered List",
    loadingPresentation: "Loading presentation...",
    pdfLoadFailed: "Failed to load PDF",
    chatPlaceholder: "Ask me anything...",
    cancel: "Cancel",
    chatEmptyLine1: "Create an outline",
    chatEmptyLine2: "to start chatting with AI",
    outlineReady: "Your outline is ready! You can edit the outline directly, or just ask me to do it.",
    steps: {
      indexing: "Indexing documents",
      planningStructure: "Planning slide structure",
      generatingContent: "Generating slide content",
      readingChapters: "Reading the memo",
      planningStoryline: "Planning the storyline",
      writingSlides: "Writing the slides"
    },
    status: {
      noOutlineTitle: "No Outline Yet",
      noOutlineDescription: "Generate an outline first",
      upToDateTitle: "Source & Instructions up to date",
      upToDateDescription: "Your outline reflects the latest source and instructions.",
      sourceChangedTitle: "Source Changed",
      sourceChangedDescription: "The source document was modified. Refresh to keep your outline in sync.",
      instructionsChangedTitle: "Instructions Changed",
      instructionsChangedDescription: "The outline instructions were modified. Refresh to keep your outline in sync.",
      bothChangedTitle: "Source & Instructions Changed",
      bothChangedDescription:
        "The source document and instructions were modified. Refresh to keep your outline in sync."
    },
    notifications: {
      refresh: "Refresh",
      refreshMessage: "Refresh the outline",
      keepCurrent: "Keep Current",
      keepCurrentMessage: "Keep the current outline",
      keptResponse:
        "Got it, keeping your current outline. You can refresh it anytime from the toolbar or simply ask me.",
      sourceChangedContent: "I detected changes in your source document. Here's what was updated:",
      instructionsChangedContent: "I detected changes in your outline instructions.",
      bothChangedContent:
        "I detected changes in your source document and outline instructions. Here's what was updated:",
      sourceModified: "Source document modified",
      instructionsUpdated: "Outline instructions updated",
      refreshQuestion: "Would you like me to refresh the outline to reflect these changes?"
    }
  },
  presentationBuilder: {
    pageTitle: "Presentation",
    title: "Presentation Builder",
    emptyTitle: "Build a presentation from this project",
    emptyDescription: "Leo turns the project into a designed slide deck.",
    outlineInstructions: "Presentation instructions",
    updatingSlides: "Updating slides…",
    createSlides: "Create slides",
    refreshSlides: "Refresh slides",
    creatingSlides: "Creating slides…",
    creatingDescription: "Leo is reading the project and designing the slides. This takes a few minutes."
  }
} as const;

export const createProject = {
  chatTitle: "Chat",
  newProjectTitle: "New Project",
  intro:
    "I'll ask a few questions and build out your project as you answer. Every field is optional — the more you share, the sharper the analysis",
  languageQuestion:
    "I'll ask a few questions and build out your project as you answer. Every field is optional — the more you share, the sharper the analysis\n\nTo start, choose your preferred language:",
  autoLanguageMsg: "Your project will be in English.",
  decisionQuestion: "Now tell me about the decision you're working on.",
  uploadFiles: "Do you have any reports, data, or documents you'd like to share?",
  decisionFromDocuments:
    "From your documents, it looks like you're deciding: **{{decision}}** Is that right, or how would you put it?",
  setupCompleteFiles: "{{fileCount}} file(s) added.",
  setupCompleteLinks: "{{linkCount}} link(s) added.",
  setupCompleteFilesAndLinks: "{{fileCount}} file(s) and {{linkCount}} link(s) added.",
  setupComplete: "Setup complete! Now tell me about the decision you're working on.",
  projectSetupComplete:
    "Project Setup complete! Continue to the next step, Case Framing, to finish creating your project.",
  nextStep: "Next Step",
  navNextCaseFrame: "Next: Case Frame",
  navConfirmed: "Confirmed",
  confirmSetup: "Confirm",
  walkthroughFormat: "This looks like a **{{value}}** decision. Is that right?",
  walkthroughScope: "On scope, here's what I took from your documents: {{value}} Does that match?",
  walkthroughIntent: "And what's driving it: {{value}} Is that right?",
  walkthroughSectionFilled: "I've filled in {{section}} from your documents.",
  walkthroughWriting: "Writing {{section}}",
  documentsRead_one: "✓ Read {{count}} document",
  documentsRead_other: "✓ Read {{count}} documents",
  documentsReadPartial: "✓ Read {{count}} of {{total}} documents",
  walkthroughChecking: "Checking {{section}} against your answers",
  walkthroughSectionFilledAdjusted:
    "I've filled in {{section}} from your documents and adjusted it to match your earlier answers.",
  walkthroughGeneralCheck: "Anything in {{section}} that doesn't look right?",
  walkthroughLooksRight: "Looks right",
  walkthroughReady:
    "Your brief has enough to launch the analysis. Launch now, or keep going through the remaining questions?",
  walkthroughLaunchNow: "Launch now",
  walkthroughKeepGoing: "Keep going",
  walkthroughFinishing:
    "Before launching, I'll fill in the sections that are still empty from your documents. What you've written stays as it is.",
  walkthroughFinishingStatus: "Finishing the brief",
  walkthroughDone:
    "Thanks, that covers your documents. You can launch the analysis now, or tell me anything else you'd like to add.",
  walkthroughDoneWithGaps:
    "Thanks, that's what I could take from your documents. {{gaps}} You can launch the analysis now, or tell me anything else you'd like to add.",
  walkthroughUnreadFiles: "I couldn't read {{files}}.",
  walkthroughUnwrittenSections: "I couldn't fill in {{sections}} from them.",
  walkthroughReadIncomplete: "I couldn't finish reading them, so some sections may still be empty.",
  walkthroughAnythingElse: "Anything you'd like to add or correct before we go deeper?",
  navBackProjectSetup: "Back: Project Setup",
  backAtProjectSetupPrefix: "You're back at Project Setup.",
  backAtProjectSetupAllSet:
    "You're back at Project Setup. Everything here is set — let me know if you'd like to make any changes.",
  backAtProjectSetupNoChanges: "Perfect, no changes it is. Let's continue to Case Framing.",
  navCaseFrameResume: "You're in Case Frame — ask me anything about your project, or refine the brief on the left.",
  filesGotIt: "Got it! You can add files or links anytime, right here in the chat.",
  vaultSuccess: "Your file has been successfully added to the Vault.",
  vaultLinkSuccess: "Your link has been successfully added to the Vault.",
  refineIntro: "Hi! I'm here to help you refine your project frame. What would you like to change?",
  versionDraftReady: "Your draft is ready. Make your changes and launch the analysis when you're ready.",
  essentialsCovered: "You've covered the essentials. That's enough to start — or add more to get deeper insights.",
  skipForNow: "Skip for now",
  upload: "Upload",
  webLink: "Web Link",
  skip: "Skip",
  save: "Save",
  confirmDecision: "Yes, that's right",
  otherDecision: "Other",
  confirmDecisionReply: "Yes, that's right.",
  launchAnalysis: "Launch Analysis",
  keepEditing: "Keep Editing",
  keepEditingPrompt: "Let's keep refining the case brief. Ask me the next question that would strengthen it the most.",
  failedUpload: "Failed to upload: {{name}}",
  failedOpen: "Couldn't open: {{name}}",
  draftUploadLimit: "Documents for one project can add up to 100 MB. Not uploaded: {{names}}",
  dimensionDropdown: "What {{name}} fits this project best?",
  dimensionText: "What's the {{name}} of this project?",
  dimensionConfirm: "Confirm",
  dimensionSkip: "Skip",
  send: "Send",
  composerPlaceholder: "Type a message to AI...",
  composerParkedPlaceholder: "Use the buttons above to continue...",
  titles: {
    projectSetup: "Project Setup",
    caseFrame: "Case Frame",
    newVersion: "New Version",
    reviewRelaunch: "Review & Relaunch"
  },
  header: {
    percentComplete: "{{value}}% Complete",
    readyToLaunch: "Ready to launch",
    launchNudge: "You've covered the essentials. Launch whenever you're ready, or keep going to sharpen the analysis.",
    launchNudgeDismiss: "Got it",
    back: "Back",
    launching: "Launching...",
    resetProject: "Reset project",
    analysisSettings: "Analysis settings",
    setupIncompleteTitle: "Setup Incomplete",
    setupIncompleteDescription: "Add a project name to continue — the minimum we need to get started",
    setupAnswerInChatDescription: "Answer the question in the chat to continue",
    frameEmptyTitle: "Project Frame Empty",
    frameEmptyDescription: "Add a project name and language — the minimum we need to get started.",
    frameIncompleteTitle: "Project Frame Incomplete",
    frameIncompleteDescription: "The more context you share in chat, the more accurate your analysis will be.",
    frameCompleteTitle: "Project Frame Complete",
    frameCompleteDescription: "The project frame has everything we need to run a thorough and accurate analysis.",
    resetTitle: "Reset Project",
    resetDescription:
      "By resetting, all your project information will be cleared. You'll need to fill in all the everything again. Are you sure?",
    resetConfirm: "Reset",
    launchIncompleteTitle: "Incomplete Brief",
    launchIncompleteDescription:
      "By launching analysis with an incomplete brief, your results may be less accurate or miss key insights.",
    launchCompleteTitle: "One Last Thing",
    launchCompleteDescription:
      "Take a quick look at your brief before launching. It drives the quality of your results."
  },
  status: {
    analyzingMessage: "Analyzing your message...",
    readingDocuments: "Reading documents...",
    readingDocumentsProgress: "Reading documents ({{done}}/{{total}})",
    searchingVault: "Searching uploaded documents...",
    searchingWeb: "Searching the web...",
    updatingBrief: "Updating case frame...",
    craftingResponse: "Crafting response..."
  },
  documentStatus: {
    reading: "Reading…",
    read: "Added to case brief",
    unreadable: "Couldn't read this file"
  },
  launch: {
    launched: "Analysis launched",
    failed: "Failed to launch analysis. Please try again."
  },
  instructionsSaveFailed: "Failed to save instructions. They are kept locally but not persisted.",
  chatError: "Failed to get response. Please try again.",
  chatErrorMessage: "Sorry, something went wrong. Please try again.",
  fileStorageFailed: "Failed to initialize file storage",
  fileStorageNotReady: "File storage is not ready yet. Please wait a moment.",
  saveFilesFailed: "Failed to save files. Please try again.",
  chatInitFailed: "Failed to initialize chat. Please try again.",
  draftLoadFailed: "Could not load draft",
  newProject: {
    loading: "Loading…",
    title: "New Project",
    untitled: "Untitled Project",
    vault: "Vault",
    cancel: "Cancel",
    close: "Close",
    launch: "Launch Analysis",
    launching: "Launching…",
    launchBlockedDecision: "To launch the analysis, confirm your decision first.",
    launchBlockedName: "To launch the analysis, give the project a name first.",
    launchReadyTitle: "Ready to launch?",
    launchReadyDescription:
      "Your case frame shapes every result, so give it a quick look before you start. The analysis takes about 3 hours.",
    launchEarlyTitle: "Launch analysis now?",
    launchEarlyDescription:
      "Your case frame isn’t finished, so the results may be less accurate. The analysis takes about 3 hours.",
    reviewFrame: "Review Frame",
    keepGoing: "Keep Going",
    launchAnyway: "Launch Anyway",
    caseFrame: "Case Frame",
    completeness: "Completeness",
    completenessValue: "{{percent}}% complete",
    sectionsSettled: "{{settled}} of {{total}} sections settled",
    sectionActive: "Leo is asking about this section",
    sectionSettled: "Settled",
    sectionOpenCount: "{{count}} open",
    sectionPlaceholder: "Write this section…",
    analysisSettings: "Analysis settings",
    generationModel: "Generation model",
    skipAnalysis: "Skip analysis generation",
    skipAnalysisNote: "Documents are still parsed when the analysis is skipped.",
    skipChapters: "Skip chapter generation",
    skipChaptersNote: "Documents are still parsed and business models generated when chapter generation is skipped.",
    analysisType: "What type of analysis are you doing?",
    analysisTypePlaceholder: "Choose…",
    saveChanges: "Save Changes",
    linkTitle: "Add a web link",
    linkLabel: "Link",
    linkPlaceholder: "https://",
    linkInvalid: "That doesn’t look like a link.",
    addLink: "Add Link",
    addFiles: "Add Files",
    addWebLink: "Add Web Link",
    sourceRead: "Read",
    sourceReading: "Reading",
    remove: "Remove",
    removeSource: "Remove {{name}}",
    dropFiles: "Drop files here",
    dropHint: "PDF, Word, PowerPoint or Excel, or a web link.",
    projectName: "Project name",
    description: "Description",
    descriptionPlaceholder: "Add a short description",
    uploadPicture: "Upload picture",
    removePicture: "Remove picture",
    language: "Language",
    version: "Version",
    visibility: "Visibility",
    organization: "Organization",
    private: "Private",
    showDetails: "Show the project details",
    languagePlaceholder: "Choose…",
    notSet: "Not set",
    shufflePicture: "Shuffle picture",
    attachFile: "Attach a file",
    tags: {
      addTag: "Add Tag",
      tagWithValue: "{{name}}: {{value}}",
      removeTagFromProject: "Remove {{tag}} from the project",
      removeFromProject: "Remove from the project",
      allTagsOnProject: "All your organization’s tags are on this project.",
      noTagsYet: "Your organization has no tags yet.",
      manageTags: "Manage Tags",
      backToTags: "Back to tags",
      edit: "Edit",
      optionsOf: "{{name}} options",
      current: "Current",
      noOptions: "No options yet.",
      noOptionsAddWithEdit: "No options yet. Add some with Edit.",
      setTagValue: "Set {{name}}",
      typeTagValue: "Type {{name}}",
      pressEnter: "Press Enter",
      manageTagsTitle: "Manage tags",
      tagChangesDescription: "Changes apply to every project in your organization.",
      createNewTag: "Create New Tag",
      done: "Done",
      deleteTagForEveryone: "Delete {{name}} for everyone",
      deleteForEveryone: "Delete for everyone",
      deleteTagTitle: "Delete “{{name}}” for everyone?",
      deleteTagDescription: "It will be removed from every project that has it, and won’t be offered again.",
      deleteTagDescriptionHere:
        "It will be removed from every project that has it, this one included, and won’t be offered again.",
      editTagTitle: "Edit “{{name}}”",
      newTagTitle: "New tag",
      newTagDescription: "Every project in your organization can use it.",
      tagName: "Name",
      tagNamePlaceholder: "e.g. Stage, Region, Deal lead",
      tagNameTaken: "Your organization already has a “{{name}}” tag.",
      tagType: "Type",
      tagTypeGroup: "Tag type",
      tagTypeOptions: "Options",
      tagTypeText: "Text",
      tagTypeOptionsHint: "Each project picks one of these options.",
      tagTypeTextHint: "Each project types its own value.",
      options: "Options",
      removeOption: "Remove {{option}}",
      addOption: "Add option",
      newOption: "New option",
      addAnotherOption: "Add another option",
      typeAnOption: "Type an option",
      removedOptionWarning: "Projects that picked a removed option lose this tag.",
      anyText: "Any text",
      noOptionsSummary: "No options yet",
      delete: "Delete",
      createTag: "Create Tag",
      saveFailed: "Couldn't save the tag",
      deleteFailed: "Couldn't delete the tag"
    }
  },
  leo: {
    welcomeTitle: "Let's create your project together",
    welcomeDescription: "I'll guide you through it here, one section at a time.",
    welcomePrompt: "Start with your files or links.",
    addFiles: "Add Files",
    addWebLink: "Add Web Link",
    remove: "Remove {{name}}",
    uploading: "Uploading…",
    thinking: "Thinking",
    conversation: "Conversation with Leo",
    panel: "Leo, Innovera intelligence",
    hide: "Hide Leo",
    close: "Close Leo",
    ask: "Ask Leo",
    askWaiting: "Ask Leo, waiting for your reply",
    waiting: "Leo is waiting for you",
    dismiss: "Dismiss",
    closeOverlay: "Close",
    message: "Message Leo",
    attach: "Attach",
    files: "Files",
    webLink: "Web Link",
    send: "Send",
    disclaimer: "Leo can make mistakes. Check important findings.",
    busy: "Leo is working, you can send when it's done.",
    more: "+{{count}} more",
    done: "Done",
    scrollToEnd: "Scroll to end",
    replyPlaceholder: "Reply to Leo",
    typePlaceholder: "Type your answer…",
    orTypePlaceholder: "Or type your answer…",
    changePlaceholder: "Type the change…",
    askPlaceholder: "Ask Leo anything…",
    changeIt: "No, change it",
    changeItReply: "No, change it.",
    changePrompt: "What would you like to change it to?",
    messageError: "There was a problem displaying this message.",
    setupComplete: "That's the setup. Confirm, and we'll go through the Case Frame together."
  },
  editor: {
    placeholder: "Start writing your project frame...",
    projectName: "Project Name",
    projectDescription: "Project description",
    undo: "Undo",
    redo: "Redo",
    bold: "Bold",
    italic: "Italic",
    underline: "Underline"
  }
} as const;

export const settings = {
  title: "Settings",
  groups: {
    account: "Account",
    organization: "Organization"
  },
  tabs: {
    profile: "Profile",
    preferences: "Preferences",
    general: "General",
    team: "Team"
  },
  common: {
    save: "Save changes",
    saving: "Saving...",
    cancel: "Cancel",
    show: "Show",
    hide: "Hide"
  },
  profile: {
    heading: "Profile",
    uploadPhoto: "Upload Photo",
    removePhoto: "Remove Photo",
    removing: "Removing...",
    photoHintSize: "For best results, upload a square image (400x400 px)",
    photoHintFormat: "JPG, PNG or WebP format, up to 5MB",
    firstName: "First Name",
    lastName: "Last Name",
    invalidFileType: "Invalid file type. Only JPG, PNG and WebP files are allowed.",
    fileTooLarge: "File size must not exceed 5MB.",
    photoRemoved: "Profile photo removed successfully",
    photoRemoveFailed: "Failed to remove photo. Please try again.",
    updated: "Profile updated successfully",
    updateFailed: "Failed to update profile. Please try again."
  },
  password: {
    heading: "Change Password",
    current: "Current Password",
    new: "New Password",
    confirm: "Confirm New Password",
    minLength: "At least 8 characters",
    hasNumber: "At least one number",
    hasCapital: "At least one capital letter",
    matches: "Passwords match",
    changed: "Password changed successfully.",
    changeFailed: "Failed to change password."
  },
  preferences: {
    heading: "Preferences",
    uiLanguage: "Interface language",
    uiLanguageHint:
      "The language of buttons, menus and labels. A project's own content follows the project's language.",
    saved: "Preferences saved",
    saveFailed: "Failed to save preferences"
  },
  languageName: {
    english: "English",
    japanese: "Japanese"
  },
  roles: {
    clientUser: "Member",
    clientAdmin: "Admin",
    systemAdmin: "System Admin"
  },
  accessModes: {
    edit: "Edit",
    read: "Read"
  },
  statuses: {
    active: "Active",
    pending: "Pending",
    inactive: "Inactive",
    expired: "Expired"
  },
  team: {
    you: "(You)",
    projectCount: "{{count}} projects",
    selectRow: "Select row for {{name}}",
    resendInvite: "Resend Invite",
    resending: "Resending...",
    cancelInvite: "Cancel invite",
    cancelling: "Cancelling...",
    manageAccess: "Manage Access",
    inviteCancelled: "Invitation canceled successfully",
    inviteCancelFailed: "Failed to cancel invitation",
    inviteResent: "Invitation resent successfully",
    inviteResendFailed: "Failed to resend invitation"
  },
  invite: {
    title: "Invite a member to this organization",
    memberRole: "Member Role",
    selectOption: "Select Option",
    accessMode: "Access Mode",
    selectAccessMode: "Select access mode",
    firstName: "First Name",
    firstNamePlaceholder: "Enter first name",
    lastName: "Last Name",
    lastNamePlaceholder: "Enter last name",
    email: "Email Address",
    emailPlaceholder: "Enter email address",
    send: "Send Invitation",
    sent: "Invitation sent successfully",
    sendFailed: "Failed to send invitation",
    invalidForm: "Invalid form data. Fix errors and try again.",
    roleRequired: "Role is required",
    firstNameRequired: "First name is required",
    firstNameTooShort: "First name must be at least 2 characters",
    lastNameRequired: "Last name is required",
    lastNameTooShort: "Last name must be at least 2 characters",
    emailRequired: "Email is required",
    emailInvalid: "Invalid email address"
  },
  manageAccess: {
    title: "Manage access for {{name}}",
    memberRole: "Member Role",
    selectRole: "Select role",
    accessMode: "Access Mode",
    selectAccessMode: "Select access mode",
    memberStatus: "Member Status",
    selectStatus: "Select status",
    saveRole: "Save Role",
    noChanges: "No changes applied.",
    updated: "User updated successfully",
    updateFailed: "Failed to update user. Please try again."
  },
  teamTable: {
    heading: "Team",
    searchPlaceholder: "Search Members",
    remove: "Remove",
    user: "User",
    role: "Role",
    projects: "Projects",
    accountStatus: "Account Status",
    dateAdded: "Date Added",
    actions: "Actions",
    selectAll: "Select all rows",
    previous: "Previous",
    next: "Next",
    usersSelected_one: "{{count}} User Selected",
    usersSelected_other: "{{count}} Users Selected",
    showingResults: "Showing {{shown}} of {{total}} results",
    fetchFailed: "Error fetching organization data"
  },
  general: {
    heading: "General",
    loading: "Loading organization data...",
    aiModel: "AI Model",
    uploadLogo: "Upload Logo",
    removeLogo: "Remove Logo",
    removing: "Removing...",
    logoHintSize: "For best results, upload a square image (400x400 px)",
    logoHintFormat: "JPG, PNG or WebP format, up to 5MB",
    orgName: "Organization Name",
    orgInstructions: "Organization Instructions",
    orgInstructionsPlaceholder: "Type your instructions here",
    invalidFileType: "Invalid file type. Only JPG, PNG and WebP files are allowed.",
    fileTooLarge: "File size must not exceed 5MB.",
    orgIdMissing: "Organization ID not found",
    logoRemoved: "Organization logo removed successfully",
    logoRemoveFailed: "Failed to remove logo. Please try again.",
    updated: "Organization updated successfully",
    updateFailed: "Failed to update organization. Please try again."
  }
} as const;

export const projects = {
  toolbar: {
    searchProjects: "Search projects",
    addProject: "Add project",
    clearSearch: "Clear search"
  },
  sidebar: {
    projects: "Projects"
  },
  card: {
    open: "Open",
    duplicate: "Duplicate",
    delete: "Delete",
    version: "Version",
    status: "Status",
    lastModified: "Last Modified",
    modifiedBy: "Modified By",
    createdBy: "Created by:",
    photoCredit: "Photo:",
    photoSource: "Wikimedia Commons",
    private: "Private",
    projectName: "Project Name",
    creator: "Creator",
    actions: "{{name}} actions",
    createVersion: "Create version",
    versionNamePlaceholder: "New version..."
  },
  sort: {
    default: "Sort",
    sortByHeading: "Sort by",
    orderHeading: "Order",
    orders: {
      azAsc: "A–Z",
      azDesc: "Z–A",
      newestFirst: "Newest First",
      oldestFirst: "Oldest First",
      completedFirst: "Completed First",
      draftFirst: "Draft First"
    }
  },
  filters: {
    heading: "Filter",
    more: "More",
    clearAll: "Clear all",
    notSet: "Not set",
    noValues: "No values yet",
    searchIn: "Search {{name}}",
    noMatches: "No matches"
  },
  dimensions: {
    heading: "Dimensions",
    newTitle: "New Dimension",
    editTitle: "Edit Dimension",
    nameLabel: "Name",
    namePlaceholder: "Dimension name...",
    typeLabel: "Type",
    optionsLabel: "Options",
    optionPlaceholder: "Add an option...",
    addOption: "Add",
    removeOption: "Remove {{option}}",
    create: "Create",
    save: "Save",
    editAction: "Edit",
    deleteAction: "Delete",
    dragToReorder: "Drag to reorder",
    confirmDelete: "Delete {{name}} ?",
    confirmDeleteBody: "Removes this field from all projects permanently.",
    confirmDeleteAction: "Confirm delete",
    cancelDeleteAction: "Cancel delete",
    emptyTitle: "No dimensions yet",
    emptyBody: "Add dimensions to track what matters to your projects.",
    subtitleText: "Text",
    subtitleDropdown_one: "Dropdown · {{count}} option",
    subtitleDropdown_other: "Dropdown · {{count}} options",
    types: {
      dropdown: "Dropdown",
      dropdownDescription: "Users pick from a list",
      text: "Text",
      textDescription: "Users type their own option"
    },
    confirmChangeTitle: "Confirm change",
    confirmChangeAction: "Confirm",
    affectedCount_one: "({{count}} project value will be cleared.)",
    affectedCount_other: "({{count}} project values will be cleared.)",
    toasts: {
      created: "Dimension created",
      updated: "Dimension updated",
      removed: "Dimension removed"
    },
    errors: {
      createFailed: "Failed to create",
      updateFailed: "Failed to update",
      deleteFailed: "Failed to delete",
      reorderFailed: "Failed to reorder"
    }
  },
  properties: {
    heading: "Properties",
    showAll: "Show all",
    hideAll: "Hide all",
    system: "System",
    alwaysVisible: "Always visible",
    dimensions: "Dimensions",
    noDimensions: "No dimensions",
    manageDimensions: "Manage Dimensions"
  },
  chat: {
    placeholder: "Ask a question about your projects…",
    closeAriaLabel: "Close chat",
    optionsMenuLabel: "Chat options",
    stop: "Stop",
    noResponseFallback: "Sorry, I couldn't generate a response.",
    genericErrorFallback: "Sorry, something went wrong. Please try again."
  },
  savedFilters: {
    heading: "Saved Filters",
    save: "Save",
    saveTitle: "Save filter",
    renameTitle: "Rename filter",
    nameLabel: "Name",
    namePlaceholder: "Filter name...",
    emptyTitle: "No saved filters yet",
    emptyBody: "Set up filters and save them to reuse in one click.",
    rename: "Rename",
    delete: "Delete",
    more: "More",
    created: "Saved filter created",
    renamed: "Saved filter renamed",
    deleted: '"{{name}}" deleted',
    createFailed: "Failed to save filter",
    renameFailed: "Failed to rename filter",
    deleteFailed: "Failed to delete filter"
  },
  status: {
    options: {
      framing: "Framing",
      generating: "Generating",
      complete: "Complete",
      failed: "Failed"
    },
    framingProgress: "Framing ({{percent}}%)",
    generatingProgress: "Generating ({{generated}}/{{total}})",
    completeProgress: "Complete ({{generated}}/{{total}})"
  },

  errors: {
    loadFailedTitle: "Could not load projects",
    loadFailedBody: "Something went wrong on our side. Try again in a moment.",
    retry: "Try again",
    duplicateFailed: "Failed to duplicate project",
    deleteDraftFailed: "Failed to delete draft",
    deleteProjectFailed: "Failed to delete project"
  },
  toasts: {
    projectDuplicated: "Project duplicated",
    draftDeleted: "Draft deleted",
    projectDeleted: "Project deleted"
  },
  deleteModal: {
    draftTitle: "Delete draft",
    projectTitle: "Delete project",
    draftDescription: "This action cannot be undone. The draft and all its data will be permanently deleted.",
    projectDescription:
      "This action cannot be undone. All project data, versions, and settings will be permanently deleted.",
    deleteAction: "Delete"
  }
} as const;
